import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { IconName, Modal, Size, FilledButton, StepperTitle, ComponentType, FontSize, useTheme, CodeBlock } from "@technis/ui";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { i18n } from "@lang/i18n";
import { translation } from "@lang/translation";
import { displayToast } from "@redux/toast/toast.slice";

import { GET_INSTALLATION_INFO_QUERY, InstallationInfoQueryResult } from "@services/installationService";
import { UserData } from "@services/userService";
import { downloadJSON, replaceUndefinedWithNull } from "@utils/utils";

const FILE_NANE_PREFIX = "installationData-";

type InstallationDataModalProps = {
  isModalVisible: boolean;
  onCloseModal: () => void;
  installationId: number;
  user?: UserData;
};

export const InstallationDataModal = ({ installationId, isModalVisible, onCloseModal }: InstallationDataModalProps) => {
  const dispatch = useDispatch();
  const themeColors = useTheme().theme.colors;

  const { data } = useQuery<InstallationInfoQueryResult>(GET_INSTALLATION_INFO_QUERY, { variables: { installationId } });
  const jsonToDownload = data?.getInstallationInfo;

  const [installationJSON, setInstallationJSON] = useState("");
  const fileName = `${FILE_NANE_PREFIX}${installationId}`;

  useEffect(() => {
    if (!!data) {
      setInstallationJSON(JSON.stringify(replaceUndefinedWithNull(jsonToDownload), null, 3));
    }
  }, [data]);

  const onClose = () => {
    onCloseModal();
    setInstallationJSON("");
  };

  const onClickSend = async () => {
    try {
      downloadJSON({ jsonToDownload, fileName });
      onClose();
      dispatch(
        displayToast({
          id: uuidv4(),
          text: i18n.t(translation.common.downloadSuccessMessage),
          variant: ComponentType.SUCCESS,
        }),
      );
    } catch (error) {
      dispatch(
        displayToast({
          id: uuidv4(),
          text: i18n.t(translation.common.downloadErrorMessage),
          variant: ComponentType.ERROR,
        }),
      );
    }
  };

  return (
    <Modal
      actionsElement={
        <div className="support-data-modal-footer">
          <FilledButton onClick={onClickSend} text={i18n.t(translation.common.download)} size={Size.LARGE} disabled={!installationJSON} />
        </div>
      }
      headerElement={<StepperTitle title={i18n.t(translation.installation.installationInfo)} iconName={IconName.ENVELOPE} />}
      size={Size.EXTRA_LARGE}
      shown={isModalVisible}
      onClose={onClose}
    >
      <div className="code-block-container">
        <CodeBlock color={themeColors.OLD_BASE_40} fontSize={FontSize.SM} borderColor={themeColors.OLD_BASE_40}>
          {installationJSON}
        </CodeBlock>
      </div>
    </Modal>
  );
};
